import { watch } from '@aurelia/runtime-html';
import { inject, resolve, IEventAggregator, ILogger } from 'aurelia';
import { TripApi } from '../services/trip-api';
import "../style.css";


@inject(TripApi)
export class Lastminutetrips {
    eventAggregator = resolve(IEventAggregator);
    aurelia_options = aurelia_options;
    RESOURCE_BASE_URL = aurelia_options.resourceBaseUrl;
    loadDataAbortController = new AbortController();
    log = resolve(ILogger).scopeTo('app.Lastminutetrips');
    abortController = new AbortController();
    isMobile = false;
    mediaQuery = null;
    dataIsLoading = false;

    // store query params - parse them, when filter has all the data - mealplans, destinations, cities etc
    queryParams = {};
    // data we are working with - last minute trip packages
    trips = [];
    searchParametersValues = {
        destinationSelection: [],
        dateSelection: new Date(Date.now() + 60 * 60 * 24 * 1000).toISOString().substring(0, 10),
        durationSelection: 0,
        priceRangeSelection: { min: 0, max: 3000 },
        hotelRatingSelection: 0,
        mealplanSelection: 0,
        departureSelection: 0,
        tagSelection: '',

        pagination: {
            itemCount: 0,
            pageSize: 100,
            pageNumber: 1,
        },
        sortOrder: 'departure-up',
        hotelInfoParams: {
            hotelName: "",
            packagegroupItemId: ""
        }
    }
    supressSearchParametersValuesChangedWatcher = false;

    constructor(tripApi) {
        this.tripApi = tripApi;

        this.searchOptions = {
            destination: [],
            duration: [
                {
                    text: aurelia_options.translations.lmt_filter_duration_header_all,
                    value: 0
                },
                {
                    text: aurelia_options.translations.lmt_filter_duration_header_prefix + ' 7 ' + aurelia_options.translations.lmt_filter_duration_header_suffix,
                    value: 7
                },
                {
                    text: aurelia_options.translations.lmt_filter_duration_header_prefix + ' 11 ' + aurelia_options.translations.lmt_filter_duration_header_suffix,
                    value: 11
                },
                {
                    text: aurelia_options.translations.lmt_filter_duration_header_prefix + ' 14 ' + aurelia_options.translations.lmt_filter_duration_header_suffix,
                    value: 14
                }
            ],
            hotelRating: [
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_all,
                    value: 0
                },
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_prefix + ' 1*',
                    value: 1
                },
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_prefix + ' 2*',
                    value: 2
                },
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_prefix + ' 3*',
                    value: 3
                },
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_prefix + ' 4*',
                    value: 4
                },
                {
                    text: aurelia_options.translations.lmt_filter_hotelrating_header_prefix + ' 5*',
                    value: 5
                }
            ],
            mealplan: [
                { 
                    text: aurelia_options.translations.lmt_filter_mealplan_header_all,
                    value: 0
                }
            ],
            departure: [
                {
                    text: aurelia_options.translations.lmt_filter_departure_header_all,
                    value: 0,
                }
            ],
            tag: [
                { text: aurelia_options.translations.lmt_filter_tag_option_all, value: '' },
                { text: aurelia_options.translations.lmt_filter_tag_option_recommended, value: "recommended" },
                { text: aurelia_options.translations.lmt_filter_tag_option_family, value: "family" },
                { text: aurelia_options.translations.lmt_filter_tag_option_luxury, value: "luxury" },
            ]
        };
    }

    attached() {
        window.addEventListener('keydown', (e) => {
          if (e.key === "Escape") {
            let dropdownsToClose = Array.from(document.querySelectorAll('dialog[role=dropdown]'));
            for (const dropdown of dropdownsToClose) {
              dropdown.close();
            }
          }
        }, { signal: this.abortController.signal })

        window.addEventListener('mousedown', (event) => {
          let dropdownsToClose = Array.from(document.querySelectorAll('dialog[role=dropdown]'))
            .filter(d => d !== event.target.closest('dialog')) // ignore clicks INSIDE dialog, clicks ON dialog need to be handled separately
            .filter(d => {  // prevent clicking on dropdown toggle from closing the dropdown, that needs to be handled by the toggle
              let toggleTarget = event.target.closest('[data-dropdown-target]')?.getAttribute('data-dropdown-target') ?? '';
              if (toggleTarget !== '') {
                return d.id !== toggleTarget;
              }
              return true;
            });
          for (const dropdown of dropdownsToClose) {
            // Did the event happen on the dialog or on modal dialog backdrop?
            let rect = dropdown.getBoundingClientRect();
            let isInDialog = (rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
              rect.left <= event.clientX && event.clientX <= rect.left + rect.width);
            if (!isInDialog) {
              dropdown.close();
            }
          }
        }, { signal: this.abortController.signal });

        this.mediaQuery = window.matchMedia('(min-width: 900px)');
        this.isMobile = !this.mediaQuery.matches;
        this.mediaQuery.addEventListener('change', this.onMatchMediaChange, { signal: this.abortController.signal });
    }

    detached() {
        this.abortController.abort();
    }

    binding() {
        this.log.debug('binding');
        // save the params, parse after filterdata is loaded
        this.queryParams = Object.fromEntries(new URLSearchParams(location.search));
        this.tripApi.getFilterData(aurelia_options.language).then(filterData => {
            this.log.debug('binding filterData', filterData);

            // load departure cities into list
            this.searchOptions.departure = [
                {
                    text: aurelia_options.translations.lmt_filter_departure_header_all,
                    value: 0,
                }, 
                ...filterData.departure_from_city.map(departureCity => ({
                    text: departureCity.lang[aurelia_options.language] ?? departureCity.lang.default,
                    value: departureCity.value,
                }))
            ];

            // load destinations
            this.searchOptions.destination = filterData.destinations.map(destination => ({
                country: {
                    text: destination.country[aurelia_options.language] ?? destination.country.default,
                    value: destination.country.default
                },
                resorts: destination.resort.map(resort => ({
                    text: resort[aurelia_options.language] ?? resort.default,
                    value: destination.country.default + '_' + resort.default
                }))
            }));

            // load mealplans and add them to translations also
            this.searchOptions.mealplan = [
                { text: aurelia_options.translations.lmt_filter_mealplan_header_all, value: 0 },
                ...filterData.mealplans.map((mealplan) => ({
                    text: (aurelia_options.translations.lmt_filter_mealplan_header_prefix + ' ' + mealplan.hotelboard + ' ' + aurelia_options.translations.lmt_filter_mealplan_header_suffix).trim(),
                    value: mealplan.sort
                }))
            ];
            for (const mealplan of filterData.mealplans) {
                this.aurelia_options.translations.lmt_mealplans[mealplan.sort] = mealplan.hotelboard;
            }

            this.searchOptions.duration = [{
                    text: aurelia_options.translations.lmt_filter_duration_header_all,
                    value: 0
                },
                ...filterData.duration.map(duration => ({
                    text: aurelia_options.translations.lmt_filter_duration_header_prefix + ` ${duration} ` + aurelia_options.translations.lmt_filter_duration_header_suffix,
                    value: duration
                }))
            ];

            // filter preselected based on query string
            if (!this.parseQueryString(this.queryParams)) {
                this.loadData(true);
            }
        });
    }

    onMatchMediaChange = (e) => {
        this.isMobile = !e.matches;
    };

    @watch((thiz) => JSON.stringify(thiz.searchParametersValues))
    async onSearchParametersValuesChanged(newValue, oldValue) {
        // ignore loadData triggering pagination itemCount value change
        if (this.supressSearchParametersValuesChangedWatcher) {
            this.supressSearchParametersValuesChangedWatcher = false;
            return;
        }
        newValue = JSON.parse(newValue);
        oldValue = JSON.parse(oldValue);

        let switchedPaginationPage = newValue.pagination.pageNumber !== oldValue.pagination.pageNumber;
        if (switchedPaginationPage) {
            this.loadData(!switchedPaginationPage);
        } else {
            this.supressSearchParametersValuesChangedWatcher = true;
            this.searchParametersValues.pagination = {
                itemCount: 0,
                pageSize: 100,
                pageNumber: 1,
            }
            this.loadData(true);
        }
    }

    async loadData(loadPagination) {
        // update url
        let params = {
            ...Object.fromEntries(new URLSearchParams(location.search)),  // maintain existing params (GA utm tags)
            ...this.tripApi.toUrl(this.searchParametersValues),
        };
        let urlQuery = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(x => x[1] !== undefined)));
        window.history.pushState(null, '', window.location.origin + window.location.pathname + (urlQuery.toString() ? `?${urlQuery}` : ''));

        let apiQuery = new URLSearchParams({
            ...Object.fromEntries(Object.entries(params).filter(x => x[1] !== undefined)),
            ...{
                offset: this.searchParametersValues.pagination.pageSize * (this.searchParametersValues.pagination.pageNumber - 1),
                limit: this.searchParametersValues.pagination.pageSize,
            },
            ...(loadPagination ? { get: "total" } : {})
        });


        this.trips = [];
        this.dataIsLoading = true;
        this.loadDataAbortController.abort("params changed");
        this.loadDataAbortController = new AbortController();
        // reload data from db, filtering is done in backend
        let url = aurelia_options.last_minute_trips_api_location + 'getTrips?' + apiQuery;
        let trips = await this.tripApi.fetchjson(url, { signal: this.abortController.signal });
        if (trips.pagination !== null) {
            this.supressSearchParametersValuesChangedWatcher = trips.pagination.itemCount !== this.searchParametersValues.pagination.itemCount;
            this.searchParametersValues.pagination.itemCount = trips.pagination.itemCount;
        }
        // If hotelinfo backlink with hotel_id and hotel_name params
        for (let trip of trips.result) {
            if (trip.hotel) {
                trip.hotel.selected_room_id = trip.hotel.rooms[0].id;
                trip.hotel.selected_mealplan_id = trip.hotel.rooms[0].mealplans[0].id;
            }
        }
        this.dataIsLoading = false;
        this.trips = trips.result;
    }

    parseQueryString(params) {
        // parse params to restore filter parameters
        let oldValue = JSON.stringify(this.searchParametersValues)

        // try to parse it to date object (should be in sql format)
        if (params.departure_date !== undefined && !isNaN(Date.parse(params.departure_date))) {
            this.searchParametersValues.dateSelection = new Date(params.departure_date).toISOString().substring(0, 10);
        }


        if (params.nights_min !== undefined && !isNaN(parseInt(params.nights_min)) && parseInt(params.nights_min) > 0) {
            params.nights_min = parseInt(params.nights_min);
            
            for (const duration of this.searchOptions.duration) {
                if (params.nights_min == duration.value) {
                    this.searchParametersValues.durationSelection = duration.value;
                }
            }
        }


        if (params.price_min !== undefined && !isNaN(parseInt(params.price_min)) && parseInt(params.price_min) >= 0) {
            params.price_min = parseInt(params.price_min);
        } else {
            params.price_min = this.searchParametersValues.priceRangeSelection.min;
        }
        if (params.price_max !== undefined && !isNaN(parseInt(params.price_max)) && parseInt(params.price_max) >= 0) {
            params.price_max = parseInt(params.price_max);
        } else {
            params.price_max = this.searchParametersValues.priceRangeSelection.max;
        }
        let clamp = (number, min, max) => Math.max(min, Math.min(number, max));
        params.price_max = clamp(params.price_max, this.searchParametersValues.priceRangeSelection.max, params.price_min)
        this.searchParametersValues.priceRangeSelection.min = params.price_min;
        this.searchParametersValues.priceRangeSelection.max = params.price_max;


        if (params.hotelrating_min !== undefined && !isNaN(parseInt(params.hotelrating_min)) && parseInt(params.hotelrating_min) > 0) {
            params.hotelrating_min = parseInt(params.hotelrating_min);

            for (const rating of this.searchOptions.hotelRating) {
                if (params.hotelrating_min == rating.value) {
                    this.searchParametersValues.hotelRatingSelection = rating.value;
                }
            }
        }


        if (params.sort_order !== undefined) {
            switch (params.sort_order) {
                case 'price-up':
                    this.searchParametersValues.sortOrder = 'price-up';
                    break;
                case 'price-down':
                    this.searchParametersValues.sortOrder = 'price-down';
                    break;
                case 'departure-down':
                    this.searchParametersValues.sortOrder = 'departure-down';
                    break;
                case 'departure-up':
                default:
                    this.searchParametersValues.sortOrder = 'departure-up';
                    break;
            }
        }

        if (params.mealplan_min !== undefined && !isNaN(parseInt(params.mealplan_min)) && parseInt(params.mealplan_min) > 0) {
            params.mealplan_min = parseInt(params.mealplan_min);

            for (const mealplan of this.searchOptions.mealplan) {
                if (params.mealplan_min == mealplan.value) {
                    this.searchParametersValues.mealplanSelection = mealplan.value;
                }
            }
        }

        if (params.departure_city !== undefined) {
            // city.value is ID so use city.text instead
            for (let city of this.searchOptions.departure) {
                if (city.text.toUpperCase() == params.departure_city.toUpperCase()) {
                    this.searchParametersValues.departureSelection = city.value;
                }
            }
        }

        // destination=Austria|Bulgaria_Golden Sands|Bulgaria_Nessebar
        if (params.destination !== undefined) {
            for (let destination of params.destination.split("|")) {
                let info = destination.split("_");
                let country = info[0];
                let resort = info[1] ?? null;

                let optionCountry = this.searchOptions.destination.find(x => x.country.value == country)
                if (! optionCountry) {
                    continue;
                }
                if (resort) {
                    let optionResort = optionCountry.resorts.find(x => x.value == destination);
                    if (optionResort) {
                        this.searchParametersValues.destinationSelection.push(destination)
                    }
                } else {
                    this.searchParametersValues.destinationSelection.push(country, ...optionCountry.resorts.map(r => r.value))
                }
            }
        }
        
        if (params.hotel_tag !== undefined && ["recommended", "luxury", "family"].includes(params.hotel_tag)) {
            this.searchParametersValues.tagSelection = params.hotel_tag;
        }


        if (params.hotel_name !== undefined) {
            this.searchParametersValues.hotelInfoParams.hotelName = params.hotel_name;
            if (params.packagegroup_item_id !== undefined) {
                this.searchParametersValues.hotelInfoParams.packagegroupItemId = params.packagegroup_item_id;
            }
        }

        let newValue = JSON.stringify(this.searchParametersValues)
        return newValue !== oldValue
    }

    resetAllFilters() {
        // reset all filters back to their initial state
        this.searchParametersValues.destinationSelection = [];
        this.searchParametersValues.dateSelection = new Date(Date.now() + 60 * 60 * 24 * 1000).toISOString().substring(0, 10);
        this.searchParametersValues.durationSelection = 0;
        this.searchParametersValues.priceRangeSelection =  { min: 0, max: 3000 };
        this.searchParametersValues.hotelRatingSelection = 0;
        this.searchParametersValues.mealplanSelection = 0;
        this.searchParametersValues.departureSelection = 0;
        this.searchParametersValues.tagSelection = '';
        this.searchParametersValues.sortOrder = 'departure-up';
    }

    openModalFilter() {
        document.querySelector('#js-filters-lmt-modal-container').showModal();
    }

    closeModalFilter() {
        document.querySelector('#js-filters-lmt-modal-container').close();
    }

    removeHotelSearchParams() {
        this.searchParametersValues.hotelInfoParams.hotelName = "";
        this.searchParametersValues.hotelInfoParams.packagegroupItemId = ""
    }
}